import React from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import type { LiveQueueSummary } from '../../services/api';

interface LiveQueuePillProps {
  summary: LiveQueueSummary | null;
}

const LiveQueuePill: React.FC<LiveQueuePillProps> = ({ summary }) => {
  if (!summary?.liveStatusEnabled) return null;
  const rows = summary.rows.filter((row) => row.label || row.value);
  if (rows.length === 0) return null;

  return (
    <Paper
      role="status"
      aria-label="Live queue information"
      elevation={5}
      sx={{
        position: 'fixed',
        // The app's FABs occupy two stacked slots from the bottom-right.
        // Keep the pill directly above the top FAB with a small visual gap.
        bottom: { xs: 'calc(env(safe-area-inset-bottom) + 192px)', md: 160 },
        right: { xs: 16, sm: 24 },
        zIndex: (theme) => theme.zIndex.appBar - 1,
        width: { xs: 105, sm: 140 },
        height: { xs: 94, sm: 125 },
        boxSizing: 'border-box',
        borderRadius: 3,
        px: { xs: 0.5, sm: 0.75 },
        py: { xs: 0.4, sm: 0.6 },
        background: 'linear-gradient(135deg, rgba(10, 88, 110, 0.98), rgba(76, 42, 140, 0.98))',
        color: '#fff',
        pointerEvents: 'none',
        border: '1px solid rgba(255, 255, 255, 0.38)',
        boxShadow: '0 8px 24px rgba(42, 25, 105, 0.38), 0 0 0 1px rgba(76, 224, 255, 0.2)',
      }}
    >
      {rows.map((row, index) => (
        <React.Fragment key={`${row.label ?? 'row'}-${index}`}>
          {index > 0 && (
            <Divider sx={{ my: { xs: 0.35, sm: 0.55 }, borderColor: 'rgba(255, 255, 255, 0.14)' }} />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.35, sm: 0.55 },
              minHeight: { xs: 36, sm: 49 },
            }}
          >
            <Typography
              component="span"
              sx={{
                color: 'rgba(255,255,255,0.76)',
                fontSize: { xs: '0.56rem', sm: '0.64rem' },
                fontWeight: 700,
                letterSpacing: '0.09em',
                lineHeight: 1.1,
                textTransform: 'uppercase',
                overflowWrap: 'anywhere',
                textAlign: 'center',
              }}
            >
              {row.label ?? ''}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: '#fff',
                fontSize: { xs: '1.3rem', sm: '1.55rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1,
                textAlign: 'center',
                overflowWrap: 'anywhere',
              }}
            >
              {row.value ?? ''}
            </Typography>
          </Box>
        </React.Fragment>
      ))}
    </Paper>
  );
};

export default LiveQueuePill;
